import { CityDataType } from "@website/accommodation-ui/hotel-search";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import { DatepickerSelectedDateType } from "@website/ui/datepicker";
import { dateFormat } from "@website/utils/date-format";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { ParsedUrlQueryInput } from "node:querystring";
import { useCallback, useMemo, useState } from "react";
import { fetchSearchTextData } from "./index.server";

const searchSuggestion = [
  {
    id: "6433",
    title: "تهران",
    url: "تهران",
    hotel_count: 120,
    info: "120 اقامتگاه",
    isHotel: false,
    subtitle: "تهران"
  },
  {
    id: "6497",
    title: "مشهد",
    url: "مشهد",
    hotel_count: 186,
    info: "186 اقامتگاه",
    isHotel: false,
    subtitle: "خراسان رضوی"
  },
  {
    id: "6640",
    title: "شیراز",
    url: "شیراز",
    hotel_count: 82,
    info: "82 اقامتگاه",
    isHotel: false,
    subtitle: "فارس"
  },
  {
    id: "6918",
    title: "کیش",
    url: "کیش",
    hotel_count: 28,
    info: "28 اقامتگاه",
    isHotel: false,
    subtitle: "هرمزگان"
  },
  {
    id: "6326",
    title: "اصفهان",
    url: "اصفهان",
    hotel_count: 87,
    info: "87 اقامتگاه",
    isHotel: false,
    subtitle: "اصفهان"
  },
  {
    id: "6220",
    title: "تبریز",
    url: "تبریز",
    hotel_count: 22,
    info: "22 اقامتگاه",
    isHotel: false,
    subtitle: "آذربایجان شرقی"
  },
  {
    id: "6984",
    title: "کاشان",
    url: "کاشان",
    hotel_count: 45,
    info: "45 اقامتگاه",
    isHotel: false,
    subtitle: "اصفهان"
  },
  {
    id: "6931",
    title: "قشم",
    url: "قشم",
    hotel_count: 76,
    info: "76 اقامتگاه",
    isHotel: false,
    subtitle: "هرمزگان"
  }
];

export const useHotelSearch = ({
  cityData,
  source,
  utm
}: {
  cityData?: CityDataType;
  source?: string;
  utm?: {
    source?: string;
    campaign?: string;
  };
  isPay?: boolean;
}) => {
  const router = useRouter();

  const { date_to, date_from, adults, children, no_rooms } =
    router.query as Record<string, string>;
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
  const datePickerInitialValues: DatepickerSelectedDateType = useMemo(
    () => [
      {
        year: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "yyyy"
          })
        ),
        month: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "MM"
          })
        ),
        day: Number(
          dateFormat(date_from, {
            locale: router.locale,
            numberConvert: false,
            template: "dd"
          })
        ),
        dateTime: toString(
          dateFormat(date_from, {
            locale: "en",
            template: "yyyy-MM-dd"
          })
        )
      },
      {
        year: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "yyyy"
          })
        ),
        month: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "MM"
          })
        ),
        day: Number(
          dateFormat(date_to, {
            locale: router.locale,
            numberConvert: false,
            template: "dd"
          })
        ),
        dateTime: toString(
          dateFormat(date_to, {
            locale: "en",
            template: "yyyy-MM-dd"
          })
        )
      }
    ],
    [date_from, date_to, router]
  );

  const passengerCountInitialValues = {
    adult: toNumber(adults),
    child: toNumber(children),
    room: toNumber(no_rooms)
  };

  const METHOD =
    source === "nowrouzLanding" || source === "kooche-festival"
      ? "push"
      : "replace";

  const handleSearchSubmit = useCallback(
    (
      destinationURL: string,
      date_from: string,
      date_to: string,
      adults: string,
      children: string,
      no_rooms: string,
      type: string,
      id?: number
    ) => {
      const [cityTitle, hotelTitle] = destinationURL.split("/");
      return router[METHOD](
        {
          pathname:
            source === "nowrouzLanding" ||
            (source === "kooche-festival" && !hotelTitle)
              ? `/landing/${
                  source === "nowrouzLanding" ? "nowrouz-campaign" : source
                }/[cityTitle]`
              : hotelTitle
                ? "/[cityTitle]/[hotelTitle]"
                : "/[cityTitle]",
          query: {
            date_from,
            date_to,
            adults,
            children,
            no_rooms,
            cityTitle,
            ...(router?.query?.discounted
              ? { discounted: router?.query?.["discounted"] !== "false" }
              : {}),
            ...(router?.query?.availability
              ? { availability: router?.query?.["availability"] !== "false" }
              : {}),
            ...(hotelTitle ? { hotelTitle } : []),
            ...(utm?.source ? { utm_source: utm.source } : {}),
            ...(utm?.campaign ? { utm_campaign: utm.campaign } : {})
          } as ParsedUrlQueryInput
        },
        undefined,
        {
          shallow:
            router.query.cityTitle === cityTitle &&
            router.query.hotelTitle === hotelTitle,
          scroll:
            router.query.cityTitle === cityTitle &&
            router.query.hotelTitle === hotelTitle
        }
      )
        .then(() =>
          addEventToTracking(USER_TRACKING_EVENTS.GENERAL_SEARCH, {
            product: "dom-hotel",
            city: cityTitle,
            hotel: hotelTitle,
            date_from,
            date_to,
            city_id: hotelTitle ? undefined : toNumber(id),
            hotel_id: hotelTitle ? toNumber(id) : undefined
          })
        )
        .finally(() => setIsSearchPanelOpen(false));
    },
    [METHOD, router, source, utm]
  );

  const searchAutocompleteFetcher = useCallback(
    (text: string) => fetchSearchTextData(text, router.locale),
    [router]
  );

  return {
    isSearchPanelOpen: isSearchPanelOpen,
    setIsSearchPanelOpen: setIsSearchPanelOpen,
    searchSummaryProperties: {
      cityTitle: cityData?.title,
      dateFrom: date_from,
      dateTo: date_to,
      adults,
      children,
      noRooms: no_rooms
    },
    searchPanelProperties: {
      hasBtnClose: false,
      initialCityData: cityData,
      passengerCountInitialValues,
      datePickerInitialValues,
      onClose: setIsSearchPanelOpen.bind(null, false),
      handleSearchSubmit,
      searchAutocompleteFetcher,
      searchSuggestion
    }
  };
};
